// src/App.js
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { FiChevronDown } from 'react-icons/fi';
import { FaArrowUp } from 'react-icons/fa';
import './privacy.css' // Ensure this path is correct

// --- COMPANY & PRODUCT CONSTANTS ---
const COMPANY_NAME = "Dheer Software Solutions Private Limited"; // Used ONCE in contact info
const PRODUCT_NAME = "NowNFC";
const COMPANY_WEBSITE_URL = "nownfc.com";
const PRIVACY_EMAIL = "privacy@dheersoftwaresolutions.com"; // Assuming this email is still valid
const COMPANY_ADDRESS_LINE1 = "1st 'A' Floor, Shri Ram Building, Infront of Parihar Dairy";
const COMPANY_ADDRESS_LINE2 = "Main Mahamandir Circle, Nagori Gate Kila Rd, Jodhpur, Rajasthan 342006, India";
const COMPANY_PHONE = "+91 94608 10765";
const EFFECTIVE_DATE = "December 1, 2023";


// --- THEME (CSS Variables are defined in privacy.css) ---
const themeConfig = {
 breakpoints: {
   sm: '640px',
   md: '768px',
   lg: '1024px', // Used by useMediaQuery
 },
};


// --- 1. PRIVACY POLICY DATA (FULL CONTENT - UPDATED) ---
const introText = (
 <p className="dss-pp-intro-text-paragraph">
   At <strong>{PRODUCT_NAME}</strong>, we are deeply committed to transparency and protecting your privacy. This document outlines how we handle your personal information when you interact with our {PRODUCT_NAME} services, website, and applications. Please read it carefully to understand your rights and our practices.
 </p>
);


const policySections = [
 {
   id: 'introduction',
   title: '1. Introduction: Our Commitment to Your Privacy',
   content: (
     <>
       <p>Welcome to <strong>{PRODUCT_NAME}</strong>! We are dedicated to safeguarding your privacy and ensuring the secure handling of your personal information. This <strong>{PRODUCT_NAME} Privacy Policy</strong> explains how we collect, use, disclose, and protect your data when you use our NFC (Near Field Communication) cards, related services under the {PRODUCT_NAME} brand, website (<strong>{COMPANY_WEBSITE_URL}</strong>), and mobile applications (collectively, the "Services"). Our services include contactless digital business cards, identity verification, event access solutions, and smart automation powered by NFC technology.</p>
       <p>This policy outlines our practices concerning <strong>NFC and Personal Data Protection</strong>, detailing our commitment to <strong>Secure NFC Card Usage</strong> and compliance with regulations such as GDPR and CCPA. By using our Services, you agree to the collection and use of information in accordance with this policy.</p>
     </>
   ),
 },
 {
   id: 'information-we-collect',
   title: '2. Information We Collect',
   content: (
     <>
       <p>We collect various types of information to provide and improve our Services to you, ensuring robust <strong>Contactless Card Security</strong> and functionality.</p>
       <h3>2.1. Personal Information You Provide:</h3>
       <p>When you register for an account, purchase our {PRODUCT_NAME} NFC cards, or use our Services, we may collect:</p>
       <ul>
         <li><strong>Identity Data:</strong> Full name, username, or similar identifier.</li>
         <li><strong>Contact Data:</strong> Email address, phone number, billing address, delivery address.</li>
         <li><strong>Professional Data:</strong> Company name, job title, professional social media links (e.g., LinkedIn) if you choose to include them on your digital business card.</li>
         <li><strong>Location Data:</strong> General location (e.g., city, country) for service provision and analytics. Precise location only with your explicit consent for specific features.</li>
         <li><strong>Account Data:</strong> Username, password (hashed), purchase history, preferences.</li>
       </ul>
       <h3>2.2. NFC Data Usage:</h3>
       <ul>
         <li><strong>Card Configuration Data:</strong> Information you choose to encode on your {PRODUCT_NAME} NFC card, such as contact details for digital business cards, website URLs, or specific commands for smart automation.</li>
         <li><strong>Contactless Tap Data:</strong> We <strong>do not store private data from individual contactless taps</strong> made by <em>your</em> card on <em>other devices</em> (e.g., someone tapping your card to get your contact info) or by <em>other cards</em> on <em>your devices</em> unless explicitly part of a service you've consented to (e.g., event check-in logs where you are the event organizer, or identity verification logs). For services like event access or identity verification, we may log tap events (e.g., timestamp, card ID, success/failure) for security and operational purposes, always with transparency and, where required, consent. The principle of <strong>Smart NFC Data Use</strong> guides our data handling.</li>
       </ul>
       <h3>2.3. Automatically Collected Information:</h3>
       <p>When you interact with our website or mobile applications, we may automatically collect:</p>
       <ul>
         <li><strong>Usage Data:</strong> Information about how you use our Services, pages visited, features used, time spent.</li>
         <li><strong>Device Information:</strong> IP address, browser type and version, operating system, device identifiers, mobile network information.</li>
         <li><strong>Cookies and Similar Technologies:</strong> We use cookies, pixels, and local storage to enhance user experience, analyze trends, and administer the Services. (See Section 7: "Cookies and Tracking Technologies").</li>
       </ul>
     </>
   ),
 },
 {
   id: 'how-we-use-info',
   title: '3. How We Use Your Information',
   content: (
     <>
       <p>We use your personal information for various purposes, grounded in providing you with exceptional <strong>Secure NFC Card Usage</strong> and services with {PRODUCT_NAME}:</p>
       <ul>
           <li><strong>To Provide and Manage Services:</strong> Fulfilling your orders for {PRODUCT_NAME} NFC cards, enabling the functionality of your digital business cards, identity verification systems, event access, and smart automation features, and creating and managing your user account.</li>
           <li><strong>To Personalize Your Experience:</strong> Tailoring content and service features to your preferences.</li>
           <li><strong>For Communication:</strong> Sending service-related updates, security alerts, support messages, and marketing communications (where you have opted-in).</li>
           <li><strong>For Analytics and Improvement:</strong> Understanding how our Services are used to improve their design, functionality, and overall user experience. This helps us refine our <strong>Smart NFC Data Use</strong> strategies.</li>
           <li><strong>For Security and Fraud Prevention:</strong> Protecting our Services, users, and business from fraudulent or illegal activities.</li>
           <li><strong>For Legal Compliance:</strong> Complying with applicable laws, regulations, and legal processes, including <strong>NFC GDPR Compliance</strong>.</li>
       </ul>
     </>
   ),
 },
 {
   id: 'gdpr-basis',
   title: '4. Legal Basis for Processing (GDPR)',
   content: (
       <>
           <p>If you are in the European Economic Area (EEA), our legal basis for collecting and using the personal information described above will depend on the personal information concerned and the specific context in which we collect it. We primarily rely on:</p>
           <ul>
               <li><strong>Consent:</strong> Where you have given us explicit consent (e.g., for marketing communications, certain cookie usage).</li>
               <li><strong>Contractual Necessity:</strong> To perform a contract with you (e.g., to provide the {PRODUCT_NAME} services you purchased).</li>
               <li><strong>Legitimate Interests:</strong> Where processing is necessary for our legitimate interests (e.g., service improvement, fraud prevention), provided these interests are not overridden by your data protection rights.</li>
               <li><strong>Legal Obligation:</strong> To comply with our legal obligations.</li>
           </ul>
       </>
   ),
 },
 {
   id: 'data-sharing',
   title: '5. Data Sharing and Disclosure',
   content: (
       <>
           <p>We do not sell your personal information. We may share your information in the following limited circumstances:</p>
           <ul>
               <li><strong>Third-Party Service Providers:</strong> We engage trusted third-party companies and individuals to perform services on our behalf (e.g., payment processors, cloud hosting providers, analytics providers like Google Analytics, Firebase, Meta Pixel, email delivery services). These providers only have access to the information necessary to perform their functions and are contractually obligated to protect your data.</li>
               <li><strong>Business Transfers:</strong> If <strong>{PRODUCT_NAME}</strong> is involved in a merger, acquisition, or asset sale, your personal information may be transferred. We will provide notice before your personal information is transferred and becomes subject to a different privacy policy.</li>
               <li><strong>Legal Requirements:</strong> We may disclose your information if required by law, subpoena, or other legal process, or if we believe in good faith that disclosure is necessary to protect our rights, protect your safety or the safety of others, investigate fraud, or respond to a government request.</li>
               <li><strong>With Your Consent:</strong> We may share your information with other third parties with your explicit consent.</li>
           </ul>
       </>
   ),
 },
 {
   id: 'your-rights',
   title: '6. Your Rights and Choices (GDPR & CCPA Compliance)',
   content: (
       <>
           <p>We are committed to ensuring you have control over your personal data. Depending on your location and applicable laws (such as GDPR for EEA residents and CCPA for California residents), you may have the following rights regarding your <strong>NFC and Personal Data Protection</strong>:</p>
           <ul>
               <li><strong>Right to Access:</strong> You can request a copy of the personal information we hold about you.</li>
               <li><strong>Right to Rectification:</strong> You can request correction of inaccurate or incomplete personal information.</li>
               <li><strong>Right to Erasure (Right to be Forgotten):</strong> You can request deletion of your personal information, subject to certain legal limitations.</li>
               <li><strong>Right to Restrict Processing:</strong> You can request that we limit the processing of your personal information.</li>
               <li><strong>Right to Data Portability:</strong> You can request to receive your personal information in a structured, commonly used, and machine-readable format, and to transmit it to another controller.</li>
               <li><strong>Right to Object:</strong> You can object to the processing of your personal information based on our legitimate interests or for direct marketing purposes.</li>
               <li><strong>Rights Related to Automated Decision Making and Profiling:</strong> You have the right not to be subject to a decision based solely on automated processing, including profiling, which produces legal effects concerning you or similarly significantly affects you.</li>
           </ul>
           <h3>CCPA Rights (for California Residents):</h3>
           <ul>
               <li><strong>Right to Know:</strong> What personal information is being collected, used, shared, or sold.</li>
               <li><strong>Right to Delete:</strong> Request deletion of personal information.</li>
               <li><strong>Right to Opt-Out (if applicable):</strong> If we were to "sell" personal information as defined by CCPA, you would have the right to opt-out. (Note: We currently state we do not sell personal information).</li>
               <li><strong>Right to Non-Discrimination:</strong> You will not be discriminated against for exercising your CCPA rights.</li>
           </ul>
           <p>To exercise any of these rights, please contact us using the details in Section 12. We will respond to your request within the timeframes mandated by applicable law.</p>
       </>
   ),
 },
 {
   id: 'cookies-tracking',
   title: '7. Cookies and Tracking Technologies',
   content: (
       <>
           <p>Our website and services use cookies and similar tracking technologies (like web beacons, pixels, and local storage) to enhance your experience, analyze usage, and for marketing purposes.</p>
           <h3>What are Cookies?</h3>
           <p>Cookies are small text files stored on your device when you visit a website.</p>
           <h3>Types of Cookies We Use:</h3>
           <ul>
               <li><strong>Essential Cookies:</strong> Necessary for the website to function and cannot be switched off.</li>
               <li><strong>Performance Cookies (Analytics):</strong> Allow us to count visits and traffic sources (e.g., Google Analytics) to measure and improve site performance.</li>
               <li><strong>Functional Cookies:</strong> Enable enhanced functionality and personalization.</li>
               <li><strong>Targeting/Advertising Cookies:</strong> May be set through our site by our advertising partners (e.g., Meta Pixel) to build a profile of your interests and show you relevant ads on other sites.</li>
           </ul>
           <h3>Managing Cookies:</h3>
           <p>You can control and manage cookies through your browser settings. Disabling certain cookies may affect the functionality of our Services. We provide a cookie consent banner for managing your preferences regarding non-essential cookies.</p>
       </>
   ),
 },
 {
   id: 'data-security',
   title: '8. Data Security: Our Priority for Contactless Card Security',
   content: (
       <>
           <p>We implement robust technical and organizational security measures to protect your personal information from unauthorized access, use, alteration, or disclosure. These measures include:</p>
           <ul>
               <li><strong>SSL/TLS Encryption:</strong> Encrypting data transmitted to and from our Services.</li>
               <li><strong>Data Encryption:</strong> Encrypting sensitive data at rest where appropriate.</li>
               <li><strong>Token-Based Security:</strong> Utilizing tokens for authentication and session management.</li>
               <li><strong>Access Controls:</strong> Limiting access to personal information to authorized personnel only.</li>
               <li><strong>Regular Security Audits:</strong> Periodically reviewing our security practices.</li>
           </ul>
           <p>Despite our best efforts, no security system is impenetrable. We cannot guarantee the absolute security of your information, but we are committed to continuously improving our <strong>Contactless Card Security</strong> measures for {PRODUCT_NAME}.</p>
       </>
   ),
 },
 {
   id: 'data-retention',
   title: '9. Data Retention',
   content: (
       <>
           <p>We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. This includes retaining data to:</p>
           <ul>
               <li>Maintain your account and provide Services.</li>
               <li>Comply with legal and regulatory obligations.</li>
               <li>Resolve disputes and enforce our agreements.</li>
           </ul>
           <p>Once your information is no longer needed, we will securely delete or anonymize it.</p>
       </>
   ),
 },
 {
   id: 'international-transfers',
   title: '10. International Data Transfers',
   content: (
       <>
           <p>Your information, including personal data, may be transferred to — and maintained on — computers located outside of your state, province, country, or other governmental jurisdiction where the data protection laws may differ. If you are located in the EEA or UK, this may mean your data is transferred outside these regions.</p>
           <p>We will take all steps reasonably necessary to ensure that your data is treated securely and in accordance with this Privacy Policy and that appropriate safeguards are in place (e.g., Standard Contractual Clauses approved by the European Commission or other relevant adequacy mechanisms).</p>
       </>
   ),
 },
 {
   id: 'childrens-privacy',
   title: '11. Children\'s Privacy (Age Restriction)',
   content: (
       <>
           <p>Our Services are not intended for individuals under the age of 13 (or a higher age threshold as required by local law, such as 16 in some EEA countries). We do not knowingly collect personal information from children under this age.</p>
           <p>If we become aware that we have inadvertently collected personal information from a child without verification of parental consent, we will take steps to delete such information from our servers promptly. If you are a parent or guardian and believe your child has provided us with personal information without your consent, please contact us using the details provided in Section 12.</p>
       </>
   ),
 },
 {
   id: 'contact-info',
   title: '12. Contact Information for Complaints and Data Access',
   content: (
       <>
           <p>If you have any questions, concerns, or complaints about this <strong>{PRODUCT_NAME} Privacy Policy</strong>, our data handling practices, or if you wish to exercise your data rights, please contact our Data Protection Officer (or privacy team) at:</p>
           <p>
               <strong>{COMPANY_NAME}</strong> {/* THIS IS THE ONLY VISIBLE INSTANCE OF THE FULL COMPANY NAME */}
               <br />
               Attn: Data Privacy Officer for {PRODUCT_NAME}<br />
               {COMPANY_ADDRESS_LINE1}<br/>
               {COMPANY_ADDRESS_LINE2}<br />
               Email: <a href={`mailto:${PRIVACY_EMAIL}`}>{PRIVACY_EMAIL}</a><br />
               Phone: <a href={`tel:${COMPANY_PHONE.replace(/\s/g, '')}`}>{COMPANY_PHONE}</a>
           </p>
           <p>We aim to resolve any inquiries or concerns about your privacy and our collection or use of your personal information. If you are in the European Union, you also have the right to lodge a complaint with a supervisory authority.</p>
       </>
   ),
 },
 {
   id: 'policy-updates',
   title: '13. Policy Updates and Changes',
   content: (
       <>
           <p>We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. We will notify you of any material changes by posting the new Privacy Policy on this page and updating the "Effective Date" at the top.</p>
           <p>For significant changes, we may also provide a more prominent notice, such as by sending an email notification to registered users or by displaying a notice on our website or application prior to the change becoming effective. We encourage you to review this <strong>{PRODUCT_NAME} Privacy Policy</strong> and general privacy statement periodically to stay informed about how we are protecting your information and ensuring <strong>Secure NFC Card Usage</strong>.</p>
           <p>Your continued use of our Services after any changes or revisions to this Privacy Policy shall indicate your agreement with the terms of such revised Privacy Policy.</p>
       </>
   ),
 },
];


// --- 2. HELPER HOOKS ---
const useMediaQuery = (query) => {
 const [matches, setMatches] = useState(false);
 useEffect(() => {
   const media = window.matchMedia(query);
   if (media.matches !== matches) setMatches(media.matches);
   const listener = () => setMatches(media.matches);
   media.addEventListener('change', listener);
   return () => media.removeEventListener('change', listener);
 }, [matches, query]);
 return matches;
};


const useScrollspy = (itemIds, offset = 0) => {
 const [activeId, setActiveId] = useState('');
 const observer = useRef(null);
 const initialSetupDone = useRef(false);

 useEffect(() => {
   const elements = itemIds.map(id => document.getElementById(id)).filter(el => el);

   if (observer.current) {
     observer.current.disconnect();
   }

   observer.current = new IntersectionObserver(
     (entries) => {
       let bestMatch = null;
       entries.forEach(entry => {
         if (entry.isIntersecting) {
           if (!bestMatch || entry.intersectionRatio > bestMatch.intersectionRatio) {
             bestMatch = entry;
           }
         }
       });
       if (bestMatch) {
         setActiveId(bestMatch.target.id);
       } else {
         const intersectingEntries = entries.filter(e => e.isIntersecting);
         if (intersectingEntries.length > 0) {
           intersectingEntries.sort((a,b) => a.boundingClientRect.top - b.boundingClientRect.top);
           setActiveId(intersectingEntries[0].target.id);
         }
       }
     },
     {
       rootMargin: `-${offset}px 0px -${window.innerHeight - offset - 200}px 0px`,
       threshold: [0.1, 0.3, 0.5, 0.7, 0.9]
     }
   );

   elements.forEach(el => {
     if (el) observer.current.observe(el);
   });
  
   if (elements.length > 0 && !initialSetupDone.current) {
       const firstVisible = elements.find(el => {
           const rect = el.getBoundingClientRect();
           return rect.top >= offset && rect.top < (window.innerHeight - (window.innerHeight - offset - 200));
       });

       if (firstVisible) {
           setActiveId(firstVisible.id);
       } else if (window.scrollY === 0 && itemIds.length > 0) {
           setActiveId(itemIds[0]);
       }
       initialSetupDone.current = true;
   }

   return () => {
     if (observer.current) {
       observer.current.disconnect();
     }
   };
 }, [itemIds, offset]);

 return activeId;
};


// --- 4. REACT COMPONENTS ---

const Hero = ({ title, subtitle }) => {
 const titleVariants = { hidden: { opacity: 0, y: 35 }, visible: { opacity: 1, y: 0, transition: { duration: 0.65, delay: 0.2, ease: "easeOut" } } };
 const subtitleVariants = { hidden: { opacity: 0, y: 35 }, visible: { opacity: 1, y: 0, transition: { duration: 0.65, delay: 0.4, ease: "easeOut" } } };
 return (
   <motion.section className="dss-pp-hero-container">
     <motion.h1 className="dss-pp-hero-title" variants={titleVariants} initial="hidden" animate="visible">{title}</motion.h1>
     {subtitle && <motion.p className="dss-pp-hero-subtitle" variants={subtitleVariants} initial="hidden" animate="visible">{subtitle}</motion.p>}
   </motion.section>
 );
};

const AnimatedSection = ({ children, delay = 0, className = '' }) => {
 const sectionVariants = {
   hidden: { opacity: 0, y: 50 },
   visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut", delay } },
 };
 const combinedClassName = `dss-pp-animated-section ${className}`.trim();
 return (
   <motion.div className={combinedClassName} variants={sectionVariants} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.1 }}>
     {children}
   </motion.div>
 );
};

const Sidebar = ({ sections }) => {
 const sectionIds = sections.map(s => s.id);
 const activeId = useScrollspy(sectionIds, 80); 
 return (
   <aside className="dss-pp-sidebar-container">
     <nav className="dss-pp-sidebar-nav">
       <h3 className="dss-pp-sidebar-title">On this page</h3>
       <ul className="dss-pp-sidebar-list">
         {sections.map((section) => (
           <li className="dss-pp-sidebar-item" key={section.id}>
             <a href={`#${section.id}`} className={`dss-pp-sidebar-link ${activeId === section.id ? 'active' : ''}`}>
               {section.title.substring(section.title.indexOf(".") + 1).trim()}
             </a>
           </li>
         ))}
       </ul>
     </nav>
   </aside>
 );
};

const PolicySectionContent = ({ id, title, content }) => (
 <AnimatedSection> 
   <section id={id} className="dss-pp-policy-section-wrapper">
     <h2>{title}</h2>
     <div>{content}</div>
   </section>
 </AnimatedSection>
);

const AccordionItem = ({ id, title, content, isOpen, onToggle }) => (
 <div className="dss-pp-accordion-wrapper">
   <motion.button
     className="dss-pp-accordion-header"
     onClick={onToggle}
     aria-expanded={isOpen}
     aria-controls={`content-${id}`}
   >
     <h3>{title}</h3>
     <motion.div
       className="dss-pp-accordion-icon"
       animate={{ rotate: isOpen ? 180 : 0 }}
       transition={{ duration: 0.3, ease: "easeInOut" }}
     >
       <FiChevronDown />
     </motion.div>
   </motion.button>
   <AnimatePresence initial={false}>
     {isOpen && (
       <motion.div
         className="dss-pp-accordion-content"
         id={`content-${id}`}
         key="content"
         initial="collapsed"
         animate="open"
         exit="collapsed"
         variants={{
           open: { opacity: 1, height: 'auto', marginTop: '0rem' },
           collapsed: { opacity: 0, height: 0, marginTop: '0rem' },
         }}
         transition={{ duration: 0.35, ease: [0.04, 0.62, 0.23, 0.98] }}
       >
         {content}
       </motion.div>
     )}
   </AnimatePresence>
 </div>
);

const BackToTopButton = () => {
 const [isVisible, setIsVisible] = useState(false);
 const toggleVisibility = () => setIsVisible(window.pageYOffset > 300);
 const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

 useEffect(() => {
   window.addEventListener('scroll', toggleVisibility, { passive: true });
   return () => window.removeEventListener('scroll', toggleVisibility);
 }, []);

 return (
   <AnimatePresence>
     {isVisible && (
       <motion.button
         className="dss-pp-back-to-top-button"
         onClick={scrollToTop}
         initial={{ opacity: 0, y: 25, scale: 0.9 }}
         animate={{ opacity: 1, y: 0, scale: 1 }}
         exit={{ opacity: 0, y: 25, scale: 0.9 }}
         transition={{ duration: 0.25, ease: "easeOut" }}
         whileHover={{ y: -4, scale: 1.05 }}
         aria-label="Scroll to top"
       >
         <FaArrowUp />
       </motion.button>
     )}
   </AnimatePresence>
 );
};


// --- 5. MAIN APP COMPONENT ---
function App() {
 const [openAccordion, setOpenAccordion] = useState(null);
 const isMobile = useMediaQuery(`(max-width: ${themeConfig.breakpoints.lg})`);

 const handleAccordionToggle = (id) => {
   setOpenAccordion(openAccordion === id ? null : id);
 };

 useEffect(() => {
 }, [isMobile]);

 return (
   <HelmetProvider>
     <div className="dss-pp-page-wrapper">
       <Helmet>
         <title>{`Privacy Policy - ${PRODUCT_NAME}`}</title>
         <meta name="description" content={`Review the Privacy Policy for ${PRODUCT_NAME} services, including NFC card usage, data protection, GDPR and CCPA compliance.`} />
         <meta name="keywords" content={`NFC Card Privacy, ${PRODUCT_NAME} Policy, Data Protection, GDPR, CCPA, Contactless Card Security, Secure NFC Card Usage, ${COMPANY_WEBSITE_URL} privacy`} />
       </Helmet>

       <main className="dss-pp-main-content-wrapper">
         <Hero
           title={`${PRODUCT_NAME} Privacy Policy`}
           subtitle={`Our commitment to protecting your personal information when you use ${PRODUCT_NAME}. Last Updated: ${EFFECTIVE_DATE}`}
         />
         <div className="dss-pp-content-layout">
           {!isMobile && <Sidebar sections={policySections} />}
           <article className="dss-pp-policy-article">
             {introText}
             {isMobile ? (
               policySections.map((section, index) => (
                 <AnimatedSection delay={index * 0.07} key={section.id}>
                     <AccordionItem
                       id={section.id}
                       title={section.title}
                       content={section.content}
                       isOpen={openAccordion === section.id}
                       onToggle={() => handleAccordionToggle(section.id)}
                     />
                 </AnimatedSection>
               ))
             ) : (
               policySections.map((section) => (
                 <PolicySectionContent
                   key={section.id}
                   id={section.id}
                   title={section.title}
                   content={section.content}
                 />
               ))
             )}
           </article>
         </div>
       </main>
       <BackToTopButton />
     </div>
   </HelmetProvider>
 );
}

export default App;